import { isRunningInNativeApp } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';
import type { NativeRecording } from '@muzly/shared';

import { encodeWav, toNativeTakeJson, toTakeJson } from '../../../audio';

import type { PcmTake, TakeFrame, TakeJson } from '../../../audio';
import type { SegmentId } from '../capture';

/**
 * takeArchive — 방금 잰 테이크를 **파일 두 개**로 내보낸다: 원음 WAV와 분석 JSON.
 *
 * **이 경로에는** 받을 서버가 없다 — `RECORD_ENDPOINT`(landing 의 `/analyze` 정밀 지표)가
 * 빈 값이다. 진단 회차 자체는 서버로 간다(제출 + 테이크 업로드, ADR-0007); 여기서 만드는
 * 것은 그것과 별개인 분석용 덤프다. 그래서 "데이터가 나온다"는 곧 브라우저에서
 * 내려받는다는 뜻이고, 배선은 디버그 콘솔의 `scoreExport.ts`·admin의 training 저장과 같다.
 *
 * **테이크는 세션에 쌓아 두고 진단이 끝날 때 한 번에 내보낸다.** 세그먼트마다 즉시
 * 내려받으면 진단 한 번에 파일 8개가 떨어지고, 재시도한 세그먼트는 어느 파일이 채택된
 * 것인지 파일명만으로는 알 수 없다. 채택되는 건 **마지막 시도**라 그것만 남긴다.
 *
 * 경로에 따라 **나가는 파일이 다르다**.
 * - `mic`(브라우저) — 표본을 들고 있으므로 WAV + JSON 둘 다 만든다.
 * - `native`(기기) — 원음은 이미 기기 샌드박스에 파일로 있다. 표본을 브리지로 끌어오면
 *   GOAL.md 불변식을 깨는 것이라, JSON이 **그 파일을 가리키기만** 한다(경로·레이트·
 *   표본 수·구멍 수). 파일 자체는 이 경로로 나가지 않는다 — 진단 테이크는 업로드가 따로
 *   가져간다(ADR-0007).
 * - `mock` — 소리가 없으므로 아무것도 안 나간다. 표본 없는 파일을 내보내면 받은 쪽이
 *   "녹음이 실패했다"와 "이 경로는 원래 원음이 없다"를 구분할 수 없다.
 */

/** 내보내는 프레임 축만 남긴다 — 왜 그 축인지는 `TakeFrame` 주석. */
const toTakeFrames = (frames: NativeF0Frame[]): TakeFrame[] =>
  frames.map((f) => ({
    t: f.timestampMs,
    f0Hz: f.f0Hz,
    midi: f.midi,
    confidence: f.confidence,
    rmsDb: f.rmsDb,
    voiced: f.voiced,
  }));

export interface ArchivedTake {
  segment: SegmentId;
  /** 브라우저 경로의 표본. 네이티브 회차에서는 없다. */
  take: PcmTake | null;
  /** 네이티브가 기기에 쓴 원음 파일. 브라우저 회차에서는 없다. */
  recording: NativeRecording | null;
  frames: NativeF0Frame[];
  /** 게이트가 잡은 방 소음 바닥(dBFS). 못 잡았으면 null — null도 싣는 값이다. */
  noiseFloorDbfs: number | null;
  recordedAt: number;
}

export interface TakeFile {
  filename: string;
  blob: Blob;
}

/**
 * 한 테이크 → 내보낼 파일 둘. 파일명은 `muzly-<세그먼트>-<시각>`으로 짝을 맞춘다 —
 * WAV와 JSON이 같은 녹음이라는 걸 파일명만 보고 알 수 있어야 한다.
 */
export const takeFiles = (
  archived: ArchivedTake,
  requestedSampleRate: number,
): TakeFile[] => {
  const stamp = new Date(archived.recordedAt)
    .toISOString()
    .replace(/[:.]/g, '-');
  const base = `muzly-${archived.segment}-${stamp}`;

  // 네이티브 회차: 원음은 **이미 기기에 파일로 있다**. 표본을 다시 브리지로 끌어와
  // WAV를 만들면 GOAL.md 불변식을 깨는 것이고, 그럴 이유도 없다 — JSON이 그 파일을
  // 가리키면 된다(경로·레이트·표본 수·구멍 수). 그래서 여기서는 WAV를 안 만든다.
  if (archived.recording) {
    const json = toNativeTakeJson({
      segment: archived.segment,
      recording: archived.recording,
      frames: toTakeFrames(archived.frames),
      recordedAt: archived.recordedAt,
    });
    return [
      {
        filename: `${base}.json`,
        blob: new Blob([JSON.stringify(json)], { type: 'application/json' }),
      },
    ];
  }

  if (!archived.take) {
    return [];
  }
  const json: TakeJson = toTakeJson({
    segment: archived.segment,
    take: archived.take,
    requestedSampleRate,
    frames: toTakeFrames(archived.frames),
    recordedAt: archived.recordedAt,
  });
  return [
    { filename: `${base}.wav`, blob: encodeWav(archived.take) },
    {
      filename: `${base}.json`,
      blob: new Blob([JSON.stringify(json)], { type: 'application/json' }),
    },
  ];
};

/**
 * 파일을 실제로 내려받는다. DOM이 없으면(테스트·네이티브 WebView) 조용히 아무것도 안 한다 —
 * 여기서 던지면 진단 완료 자체가 실패한다.
 */
export const downloadTakeFiles = (files: TakeFile[]): number => {
  if (
    typeof document === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return 0;
  }
  // **네이티브 셸에서는 내보내지 않는다.** WebView에는 다운로드 핸들러가 없어서
  // `anchor.click()`이 파일을 저장하는 대신 그 blob URL로 **이동한다** — 화면이 통째로
  // JSON 덤프로 바뀌고 사용자는 방금 있던 자리를 잃는다(2026-08-26 시뮬레이터 실측).
  // 네이티브 회차의 원음이 어디 남는지는 업로드가 붙은 뒤로 갈렸다: 올라간 테이크는
  // 서버 미디어로 남고 **기기 파일은 지워진다**(`useRecordMachine`의 업로드 성공 경로).
  // 그래서 여기서 내보내는 JSON의 `pcm.uri`는 이미 없는 경로일 수 있다 — 그 회차를 다시
  // 들으려면 서버 쪽 `media$.url`을 본다. 업로드가 실패했거나 꺼져 있으면 기기 파일이
  // 그대로 남아 예전과 같다.
  if (isRunningInNativeApp()) {
    return 0;
  }
  for (const file of files) {
    const url = URL.createObjectURL(file.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
  return files.length;
};
